import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

// Script Approval data layer. The same script workflow is driven from two
// places, so every script-level hook takes a `scope`:
//   { kind: "team", planId }                 — the logged-in team (/api/scripts)
//   { kind: "review", token, reviewerName }  — a public share link (/api/script-review)
// The workflow rules themselves live server-side (helpers/scriptWorkflow.js in
// pluginbackendnode) — nothing here re-implements them.

export const teamScope = (planId) => ({ kind: "team", planId });
export const reviewScope = (token, reviewerName) => ({ kind: "review", token, reviewerName });

const scriptPath = (scope, scriptId) =>
  scope.kind === "team" ? `/scripts/${scriptId}` : `/script-review/${scope.token}/scripts/${scriptId}`;

const boardKey = (scope) =>
  scope.kind === "team" ? ["script-board", scope.planId] : ["script-review", scope.token];
const detailKey = (scope, scriptId) => [...boardKey(scope), "script", scriptId];
const BOARDS_KEY = ["script-boards"];

const get = (url) => axios.get(url).then((r) => r.data);
const post = (url, body) => axios.post(url, body).then((r) => r.data);

export const shareUrlFor = (token) => (token ? `${window.location.origin}/script-review/${token}` : null);

export const useScriptBoards = () =>
  useQuery({ queryKey: BOARDS_KEY, queryFn: () => get("/scripts"), staleTime: 60 * 1000 });

export const useScriptBoard = (planId) =>
  useQuery({
    queryKey: boardKey(teamScope(planId)),
    queryFn: () => get(`/scripts/plans/${planId}`),
    enabled: Boolean(planId),
  });

// A bad/rotated link is a 404 — no point retrying it.
export const useScriptReview = (token) =>
  useQuery({
    queryKey: boardKey(reviewScope(token)),
    queryFn: () => get(`/script-review/${token}`),
    enabled: Boolean(token),
    retry: false,
  });

export const useScriptDetail = (scope, scriptId) =>
  useQuery({
    queryKey: detailKey(scope, scriptId),
    queryFn: () => get(scriptPath(scope, scriptId)),
    enabled: Boolean(scriptId),
    retry: false,
  });

// One workflow action (versions / decision / remarks). Every action answers
// with the fresh script detail, so the detail cache is replaced directly and
// only the row-level lists are refetched.
const useScriptAction = (scope, scriptId, action) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      post(`${scriptPath(scope, scriptId)}/${action}`, scope.kind === "review" ? { ...payload, reviewerName: scope.reviewerName } : payload),
    onSuccess: (detail) => {
      queryClient.setQueryData(detailKey(scope, scriptId), detail);
      queryClient.invalidateQueries({ queryKey: boardKey(scope) });
      if (scope.kind === "team") queryClient.invalidateQueries({ queryKey: BOARDS_KEY });
    },
    // 409 = someone else changed the script in between — pull the latest so
    // the person sees what changed instead of retrying blind.
    onError: (error) => {
      if (error?.response?.status === 409) queryClient.invalidateQueries({ queryKey: detailKey(scope, scriptId) });
    },
  });
};

export const useScriptActions = (scope, scriptId) => ({
  saveVersion: useScriptAction(scope, scriptId, "versions"),
  decide: useScriptAction(scope, scriptId, "decision"),
  addRemark: useScriptAction(scope, scriptId, "remarks"),
});

// ---- Team-only board management -------------------------------------------

const useBoardMutation = (planId, mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boardKey(teamScope(planId)) });
      queryClient.invalidateQueries({ queryKey: BOARDS_KEY });
    },
  });
};

// Same payload as Plans' add-influencers ({listId, platform, profiles}) — fed by
// the shared AddInfluencersDrawer.
export const useAddScriptCreators = (planId) =>
  useBoardMutation(planId, (payload) => post(`/scripts/plans/${planId}/creators`, payload));

export const useRemoveScript = (planId) =>
  useBoardMutation(planId, (scriptId) => axios.delete(`/scripts/${scriptId}`).then((r) => r.data));

export const useRotateCreatorLink = (planId) =>
  useBoardMutation(planId, (scriptId) => post(`/scripts/${scriptId}/share-link/rotate`));

export const useRotateBrandLink = (planId) =>
  useBoardMutation(planId, () => post(`/scripts/plans/${planId}/share-link/rotate`));
